/**
 * Typed API Client with Zod Runtime Validation
 *
 * This client provides type-safe API calls with runtime validation.
 * Use this alongside the existing apiClient.js for gradual migration.
 *
 * Usage:
 *   import { salesApi, productsApi, clientsApi } from '@/global/API/typedApiClient';
 *
 *   // Type-safe API calls with runtime validation
 *   const sale = await salesApi.createSale({ ... }); // TypeScript knows the shape
 *   const products = await productsApi.getProducts({ storeId: '123' });
 *
 * Error Handling:
 *   - In DEV: All validation errors THROW immediately (full error catching)
 *   - In PROD: Request validation throws, response validation logs only (graceful)
 */

import {
  TypedApiClient,
  createSalesApi,
  createProductsApi,
  createClientsApi,
  createStoresApi,
  createOrganizationsApi,
  createTransactionsApi,
  createSuppliesApi,
  createTransfersApi,
  createWriteoffsApi,
  createRevisionsApi,
  createSpendingsApi,
  createWorkingHoursApi,
  createAuthApi,
  // New API creators
  createProductCategoriesApi,
  createSuppliersApi,
  createWalletsApi,
  createPaymentMethodsApi,
  createRolesApi,
  createProductUnitsApi,
  createCashboxTransactionsApi,
  createPriceTypesApi,
  createUsersApi,
  createAuditLogApi,
  createSavedCartsApi,
  createChecklistsApi,
  createComplexSalesApi,
  createComplexSaleTemplatesApi,
  createSupplyOrdersApi,
  createManufacturesApi,
  createPrintTemplatesApi,
  createProductTagsApi,
  createClientOperationsApi,
  createSettingsApi,
  createOrdersApi,
  createProductHistoryApi,
  createSpendingCategoriesApi,
  createDiscountOptionsApi,
  createWriteOffReasonsApi,
  createReturnReasonsApi,
  createPurchaseMethodsApi,
  createSaleSettingsApi,
  createProductModifiersApi,
  createRecurringSpendingsApi,
  createProductMovementsApi,
  createReportsApi,
  createUpdatesApi,
  // Type exports
  type SaleResponse,
  type CreateSale,
  type DeleteResponse,
  type CartItem,
  type Variant,
  type ProductResponse,
  type ProductWithStockResponse,
  type CreateProduct,
  type ClientResponse,
  type ClientPunchCardResponse,
  type CreateClient,
  type StoreResponse,
  type CreateStore,
  type UpdateStore,
  type OrganizationResponse,
  type UpdateOrganization,
  type OrganizationComparisonResult,
  type TransactionResponse,
  type CreateTransaction,
  type SupplyResponse,
  type CreateSupply,
  type TransferResponse,
  type CreateTransfer,
  type WriteoffResponse,
  type CreateWriteoff,
  type RevisionResponse,
  type CreateRevision,
  type SpendingResponse,
  type CreateSpending,
  type WorkingHoursResponse,
  type CreateWorkingHours,
  type AuthUser,
  type Session,
  type LoginResponse,
  type SignupResponse,
} from '@atlas/api-client';
import { auth } from '../Firebase/firebase';
import type { ZodSchema } from 'zod';
import { beginWrite } from './writesInFlight';

// Get backend API URL from environment
const API_BASE_URL = process.env.EXPO_PUBLIC_BACKEND_API_URL || 'http://localhost:3000/api';

/**
 * ATL-889 — counts in-flight WRITES so ATL-888's live rollback does not restart the app on top of
 * one.
 *
 * The dual-write commits to Firestore first and then awaits the Supabase call, and the ATL-782
 * outbox only enqueues on an observed REJECTION. A runtime teardown produces no rejection, so a
 * restart landing mid-write loses the Supabase mirror silently — the exact drift class
 * (ATL-871: present in Firestore, absent in Supabase) the campaign has spent weeks eliminating.
 *
 * This subclass is the whole instrumentation, because `TypedApiClient` has exactly two paths that
 * reach the network and every endpoint helper goes through one of them:
 *   * `request()`      — every normal call. Non-GET methods are writes.
 *   * `replayRequest()` — the outbox re-issuing a captured write. Always a write in practice, and
 *                         losing a REPLAY is losing the recovery of an already-failed write.
 *
 * Instrumenting here rather than at the ~4 dual-write modules means a new write path is counted
 * automatically instead of being forgotten. GETs are deliberately not counted: nothing is lost by
 * restarting during a read.
 */
/**
 * The base `request` options, with the three schema-bound fields re-typed against this call's
 * generics. `Omit` keeps `params` and `config` exactly as the base class declares them, so this does
 * NOT have to name axios' request-config type — apps/atlas pins axios ^0.21 while the api-client
 * package resolves a newer major, and hard-coding either would silently diverge from super's real
 * signature. Deriving it means the override cannot drift, and no cast is needed to call super.
 */
type BaseRequestOptions = Parameters<TypedApiClient['request']>[2];
type WriteRequestOptions<TRequest, TResponse> = Omit<
  BaseRequestOptions,
  'data' | 'requestSchema' | 'responseSchema'
> & {
  data?: TRequest;
  requestSchema?: ZodSchema<TRequest>;
  responseSchema: ZodSchema<TResponse>;
};

class WriteCountingApiClient extends TypedApiClient {
  async request<TRequest, TResponse>(
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    path: string,
    options: WriteRequestOptions<TRequest, TResponse>,
  ): Promise<TResponse> {
    if (method === 'GET') {
      return super.request<TRequest, TResponse>(method, path, options);
    }
    const endWrite = beginWrite();
    try {
      return await super.request<TRequest, TResponse>(method, path, options);
    } finally {
      // `finally`, not `.then()`: a rejected or timed-out write must decrement too, or the count
      // leaks upward and every later drain burns the full cap. Those paths reach the outbox on
      // their own — this counter only answers "is anything still in flight".
      endWrite();
    }
  }

  // ATL-1445 P5: the override must forward `options`. It took `config` alone, so
  // `replayRequest(request, { writeMode: 'master' })` — outbox v2's send (P3) — silently lost the
  // master header on this client: a master write would have gone out as a mirror, which backend PR B1
  // refuses (409) for a Supabase-master organization and treats as a mirror otherwise.
  async replayRequest<TResponse = unknown>(
    config: Parameters<TypedApiClient['replayRequest']>[0],
    options?: Parameters<TypedApiClient['replayRequest']>[1],
  ): Promise<TResponse> {
    if (config.method === 'GET') {
      return super.replayRequest<TResponse>(config, options);
    }
    const endWrite = beginWrite();
    try {
      return await super.replayRequest<TResponse>(config, options);
    } finally {
      endWrite();
    }
  }
}

// Create typed API client with full dev error catching
const typedClient = new WriteCountingApiClient({
  baseURL: API_BASE_URL,
  // Pass forceRefresh through to Firebase: the normal path uses the cached token
  // (auto-refreshed within 5 min of expiry); the 401 retry path forces a fresh
  // token to recover from a stale bearer (ATL-526).
  getAuthToken: async (forceRefresh = false) =>
    auth.currentUser?.getIdToken(forceRefresh) ?? null,
  timeout: 30000, // 30 seconds - /api/settings aggregates 14 data sources
  // Writes get 60s (ATL-776): the 30s abort was dropping the LARGEST carts at big orgs
  // (create_sale_atomic over a 174k-row movement table) — and a client-side abort does not
  // stop the server, it only loses the outcome. Reads keep the 30s timeout above.
  writeTimeout: 60000,

  // ✅ FULL ERROR CATCHING IN DEV - throws on validation errors
  strictValidation: __DEV__,

  onResponseValidationError: (error, path) => {
    console.error(`❌ Response validation failed: ${path}`, error.errors);
    // In dev: throws (due to strictValidation)
    // In prod: logs only (graceful degradation)
  },

  onRequestValidationError: (error, path) => {
    console.error(`❌ Request validation failed: ${path}`, error.errors);
    // Always throws - prevents sending bad data to API
  },
});

// ============ TYPED API INSTANCES ============

// Core
export const salesApi = createSalesApi(typedClient);
export const productsApi = createProductsApi(typedClient);
export const clientsApi = createClientsApi(typedClient);
export const storesApi = createStoresApi(typedClient);
export const organizationsApi = createOrganizationsApi(typedClient);

// Transactions
export const transactionsApi = createTransactionsApi(typedClient);

// Inventory
export const suppliesApi = createSuppliesApi(typedClient);
export const transfersApi = createTransfersApi(typedClient);
export const writeoffsApi = createWriteoffsApi(typedClient);
export const revisionsApi = createRevisionsApi(typedClient);

// Operations
export const spendingsApi = createSpendingsApi(typedClient);
export const workingHoursApi = createWorkingHoursApi(typedClient);

// Auth
export const authApi = createAuthApi(typedClient);

// New APIs
export const productCategoriesApi = createProductCategoriesApi(typedClient);
export const suppliersApi = createSuppliersApi(typedClient);
export const walletsApi = createWalletsApi(typedClient);
export const paymentMethodsApi = createPaymentMethodsApi(typedClient);
export const rolesApi = createRolesApi(typedClient);
export const productUnitsApi = createProductUnitsApi(typedClient);
export const cashboxTransactionsApi = createCashboxTransactionsApi(typedClient);
export const priceTypesApi = createPriceTypesApi(typedClient);
export const usersApi = createUsersApi(typedClient);
export const auditLogApi = createAuditLogApi(typedClient);
export const savedCartsApi = createSavedCartsApi(typedClient);
export const checklistsApi = createChecklistsApi(typedClient);
export const complexSalesApi = createComplexSalesApi(typedClient);
export const complexSaleTemplatesApi = createComplexSaleTemplatesApi(typedClient);
export const supplyOrdersApi = createSupplyOrdersApi(typedClient);
export const manufacturesApi = createManufacturesApi(typedClient);
export const printTemplatesApi = createPrintTemplatesApi(typedClient);
export const productTagsApi = createProductTagsApi(typedClient);
export const clientOperationsApi = createClientOperationsApi(typedClient);
export const settingsApi = createSettingsApi(typedClient);
export const ordersApi = createOrdersApi(typedClient);
export const productHistoryApi = createProductHistoryApi(typedClient);
export const spendingCategoriesApi = createSpendingCategoriesApi(typedClient);
export const discountOptionsApi = createDiscountOptionsApi(typedClient);
export const writeOffReasonsApi = createWriteOffReasonsApi(typedClient);
export const returnReasonsApi = createReturnReasonsApi(typedClient);
export const purchaseMethodsApi = createPurchaseMethodsApi(typedClient);
export const saleSettingsApi = createSaleSettingsApi(typedClient);
export const productModifiersApi = createProductModifiersApi(typedClient);
export const recurringSpendingsApi = createRecurringSpendingsApi(typedClient);
export const productMovementsApi = createProductMovementsApi(typedClient);
export const reportsApi = createReportsApi(typedClient);
export const updatesApi = createUpdatesApi(typedClient);

// ============ TYPE EXPORTS ============
// Re-export types imported from @atlas/api-client
export type {
  SaleResponse,
  CreateSale,
  DeleteResponse,
  CartItem,
  Variant,
  ProductResponse,
  ProductWithStockResponse,
  CreateProduct,
  ClientResponse,
  ClientPunchCardResponse,
  CreateClient,
  StoreResponse,
  CreateStore,
  UpdateStore,
  OrganizationResponse,
  UpdateOrganization,
  OrganizationComparisonResult,
  TransactionResponse,
  CreateTransaction,
  SupplyResponse,
  CreateSupply,
  TransferResponse,
  CreateTransfer,
  WriteoffResponse,
  CreateWriteoff,
  RevisionResponse,
  CreateRevision,
  SpendingResponse,
  CreateSpending,
  WorkingHoursResponse,
  CreateWorkingHours,
  AuthUser,
  Session,
  LoginResponse,
  SignupResponse,
};

// Export the client for custom requests
export { typedClient };
